import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";

import { ThemeProvider } from "styled-components";
import { AgentGroup } from "../shared/model/AgentGroup";
import useUserTheme from "../shared/styles/hooks/useUserTheme";
import { GlobalStyle } from "../shared/styles/theme";
import { Utilities } from "../shared/utilities";
import { getLastReadNotification, getNotificationMessages } from "./actions/AppActions";
import NotificationCenter from "./components/NotificationCenter";
import { initialState, reducer } from "./reducers/AppReducer";
import { IAppReducerState } from "./shared/model";
import { FrendsLogoItem } from "./components/FrendsLogoItem";
import { Intercom } from "./components/Intercom";
import { UserItems } from "./components/UserItems";
import { NavigationItems } from "./components/NavigationItems";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import { Permissions } from "shared/model/AllPermissions";
import { sizes } from "shared/styles/helpers"
import { useWindowSize } from "shared/hooks/useWindowSize";
import { useScrollTop } from "shared/hooks/useScrollTop";
import { AgentGroupEnvironment } from "processList/model/AppPath";
import { HashMap } from "shared/model/HashMap";
import CookiePolicyDialog from "./components/CookiePolicyDialog";
import { Navbar, NavbarVariant, NavMobileExpandButton } from "./styled";
import { fetchAgentGroups } from "shared/actions/AgentGroupActions";
import AgentGroupIndicator from "./components/AgentGroupIndicator";
import { AppSettingValue } from "shared/model/AppSettings";
import { AppInsightsConnectionStringKey } from "shared/AppInsights";
import { API_MONITORING_PATH } from "apiManagement/model/ApiMonitoring";
import { API_POLICIES_SUB_PATH } from "apiPolicies/helpers/helpers";
import useNavbarColor from "./hooks/useNavbarColor";

export const releaseNoteStorageKey = "notification/releaseNote";
export const notificationStorageKey = "notification/lastMessages";
export const AppContext = React.createContext<IAppReducerState>({ appState: initialState, dispatch: () => null });



const App: React.FC<React.PropsWithChildren<INavigationProps>> = (props) => {
    const [appState, dispatch] = React.useReducer(reducer, initialState);
    const { width: windowWidth } = useWindowSize();
    const [showForDesktop, setShowForDesktop] = React.useState(true);
    const [showExpandedMobileNav, setShowExpandedMobileNav] = React.useState(false);
    const scrollTop = useScrollTop();

    const location = useLocation();
    const match = useRouteMatch<IPathParams>(props.route);
    const [themeType] = useUserTheme();

    const [environmentName, setEnvironmentName] = React.useState<string>();
    const [agentGroupName, setAgentGroupName] = React.useState<string>();
    const [agentGroups, setAgentGroups] = React.useState<AgentGroup[]>([]);
    const [userPermissions] = React.useState<{ [key: string]: boolean }>(Utilities.getAllUserPermissions());

    const navbarConfiguration = useNavbarColor({ route: props.route });

    React.useEffect(() => {
        setEnvironmentName(match?.params.environmentName);
        setAgentGroupName(match?.params.agentGroupName);
    }, [location]);

    const showAgentGroupIndicator = ((props.activeLink === LinkNames.process || props.activeLink === LinkNames.subprocess) && 94 < scrollTop);

    React.useEffect(() => {
        const fetchInitialData = async () => {
            const [coreVersion, feedbackUrl] = await Promise.allSettled([
                Utilities.getFetchAsText("api/navigation/getUIVersion"),
                Utilities.getFetch<AppSettingValue>("api/app-settings/FeedbackUrl"),
            ]);

            const hasAppInsightsKey = Utilities.getCookies()[AppInsightsConnectionStringKey] != null;
            dispatch({ type: "SET_CORE_VERSION", coreVersion: coreVersion.status === "fulfilled" ? coreVersion.value : "Failed to fetch version" });
            dispatch({ type: "SET_HAS_APP_INSIGHTS_KEY", hasAppInsightsKey: hasAppInsightsKey });
            dispatch({ type: "SET_FEEDBACKURL", feedbackUrl: feedbackUrl.status === "fulfilled" ? feedbackUrl.value : null })
        }
        fetchInitialData();
        getNotificationMessages(dispatch, notificationStorageKey);
        getLastReadNotification(dispatch, releaseNoteStorageKey);
    }, []);

    React.useEffect(() => {
        if (windowWidth !== undefined) {
            const isDesktop = sizes.laptopL < windowWidth;
            if (isDesktop) {
                setShowForDesktop(true);
                setShowExpandedMobileNav(false);
            } else {
                setShowForDesktop(false);
            }
        }
    }, [windowWidth]);

    // Fetch agent groups only when needed
    React.useEffect(() => {
        if (!agentGroups.length) {
            const getAgentGroups = async () => {
                const agentGroupsMap = await fetchAgentGroups();
                setAgentGroups(_.values(agentGroupsMap));
            }
            getAgentGroups();
        }
    }, [agentGroups]);

    // Get default agent group info if needed
    React.useEffect(() => {
        if (!agentGroups.length || (environmentName && agentGroupName)) {
            return;
        }

        if (environmentName && !agentGroupName) {
            const environmentAgentGroups = agentGroups.filter((g) => g.environment === environmentName);
            const environmentDefaultAgentGroup = environmentAgentGroups.length ? environmentAgentGroups[0] : null;
            setAgentGroupName(environmentDefaultAgentGroup?.name || '');
            return;
        }

        const defaultAgentGroup = agentGroups[0];
        setEnvironmentName(defaultAgentGroup.environment);
        setAgentGroupName(defaultAgentGroup.name);
    }, [environmentName, agentGroupName, agentGroups]);

    const currentAgentGroup = React.useMemo(() => {
        return agentGroups.find((g) => g.environment === environmentName && g.name === agentGroupName);
    }, [agentGroups, environmentName, agentGroupName])

    // Redirect to Dashboard with default Environment if environment doesn't exist
    React.useEffect(() => {
        // Skip views that don't need environment validation or have their own handling
        const shouldSkipEnvironmentValidation = [LinkNames.environment, LinkNames.producttours].some((x) => x === props.activeLink);
        if (shouldSkipEnvironmentValidation) {
            return;
        }
        if (!_.isEmpty(agentGroups) && environmentName) {
            const doesEnvironmentExist = agentGroups.some((s) => s.environment.toLowerCase() === environmentName?.toLowerCase())
            if (!doesEnvironmentExist) {
                Utilities.toastError("Environment not found, redirecting.")
                setTimeout(() => window.location.href = `${Utilities.getUrlBase()}Dashboard/Default/Default`, 3000)
            }
        }
    }, [agentGroups, environmentName]);

    const hasDecidedConsent = Utilities.getCookies()["cookieconsent_status"];
    const isNotProductTourView = props.activeLink !== LinkNames.producttours;

    // Currently we onlt check for app insights key but it could just as well be heap and or intercom. 
    // For now it's still fine to check with app insights as that will always be set for azure installations no matter what.
    const shouldShowCookiePolicyBanner = isNotProductTourView && appState.hasAppInsightsKey && (appState.showCookiePolicyBanner || !hasDecidedConsent);

    const getNavbarStyleExtras = (): React.CSSProperties => {
        const navbarColors: { visibility: Visibility } = {
            visibility: navbarConfiguration.hasFetched ? 'visible' : 'hidden',
        }
        const styleExtraBase = {
            ...navbarColors,
            ...(props.activeLink === LinkNames.producttours && { backgroundColor: 'transparent' }),
        };

        if (showForDesktop) {
            return {
                ...styleExtraBase,
                minHeight: 'initial',
            };
        }

        if (showExpandedMobileNav) {
            return {
                ...styleExtraBase,
                height: 'initial',
                minHeight: '80px',
            };
        }

        return {
            ...styleExtraBase,
            minHeight: 'initial',
        };
    };

    const handleDecisionCallback = () => {
        dispatch({ type: "SET_SHOW_COOKIE_POLICY_BANNER", showCookiePolicyBanner: false });
    }

    const showCookiePolicyBanner = () => {
        dispatch({ type: "SET_SHOW_COOKIE_POLICY_BANNER", showCookiePolicyBanner: true });
    }

    return (
        <ThemeProvider theme={{ mode: themeType }}>
            <AppContext.Provider value={{ appState, dispatch }}>
                <GlobalStyle />

                <Navbar variant={navbarConfiguration.navbarColorVariant as NavbarVariant} style={getNavbarStyleExtras()}>
                    {showForDesktop ? (
                        <>
                            <S.FlexContainer centerItems={true}>
                                <FrendsLogoItem href={`Dashboard/${environmentName}/${agentGroupName}`} />
                                <NavigationItems {...props} hasRightsFor={userPermissions} currentAgentGroup={agentGroupName} currentEnvironment={environmentName} />
                            </S.FlexContainer>

                            <S.FlexContainer centerItems={true}>
                                {props.isAuthenticated && showAgentGroupIndicator && (
                                    <AgentGroupIndicator
                                        environmentDisplayName={currentAgentGroup?.environmentDisplayName}
                                        agentGroupDisplayName={currentAgentGroup?.displayName}
                                    />
                                )}
                                {props.isAuthenticated && <NotificationCenter />}
                                <Intercom intercomAppId={props.intercomAppId} cookiesAllowed={hasDecidedConsent === "allow"} openCookieConsentBanner={showCookiePolicyBanner} />
                                <UserItems {...props} />
                            </S.FlexContainer>
                        </>
                    ) : (
                        <>
                            <S.FlexContainer isFullWidth={true} centerItems={true} justifySpaceBetween={true} gap={16}>
                                <FrendsLogoItem href={`Dashboard/${environmentName}/${agentGroupName}`} />

                                <S.FlexContainer center={true} centerItems={true}>
                                    <NavMobileExpandButton
                                        title={!showExpandedMobileNav ? 'Open expanded navigation' : 'Close expanded navigation'}
                                        onClick={() => setShowExpandedMobileNav(show => !show)}
                                    >
                                        <i className="ci-hamburger"></i>
                                    </NavMobileExpandButton>
                                </S.FlexContainer>
                            </S.FlexContainer>

                            {showExpandedMobileNav && (
                                <S.FlexContainer asColumn={true} style={{ paddingLeft: '20px' }}>
                                    <NavigationItems {...props}
                                        hasRightsFor={userPermissions}
                                        currentAgentGroup={agentGroupName}
                                        currentEnvironment={environmentName}
                                        asColumn={true}
                                    />
                                    <NotificationCenter />
                                    <Intercom intercomAppId={props.intercomAppId} cookiesAllowed={hasDecidedConsent === "allow"} openCookieConsentBanner={showCookiePolicyBanner} />
                                    <UserItems {...props} />
                                </S.FlexContainer>
                            )}
                        </>
                    )}
                </Navbar>
                {shouldShowCookiePolicyBanner && <CookiePolicyDialog onDecisionCallback={handleDecisionCallback} />}
            </AppContext.Provider>
        </ThemeProvider>
    );
};
// Make sure the links are in lowercase
// If you do not make them lowercase they will not be matched and the UI will not show the selected item
export enum LinkNames {
    "apimanagement",
    "apimonitoring",
    "apipolicies",
    "process",
    "subprocess",
    "templates",
    "environmentvariables",
    "administration",
    "environment",
    "task",
    "monitoringrules",
    "usermanagement",
    "apikeymanagement",
    "oauthapplicationmanagement",
    "dashboard",
    "producttours",
    "privateapplication",
    "aiauditlog",
}
// Please look at the comment above the enum declaration if you have added any links here. Thanks!

export const navigationBarData: INavigationBarData[] = [
    {
        href: "", activeLinkName: LinkNames.apimanagement, label: FRENDS.Localizations.ApiManagement, permission: Permissions.ApiManagementView,
        children: [
            { href: "ApiManagement", activeLinkName: LinkNames.apimanagement, label: "API Management", permission: Permissions.ApiManagementView },
            { href: "ApiManagement", activeLinkName: LinkNames.apimonitoring, label: "API Monitoring", permission: Permissions.ApiMonitoringView, subRoute: API_MONITORING_PATH },
            { href: "ApiManagement", activeLinkName: LinkNames.apipolicies, label: "API Policies", permission: Permissions.ApiPolicyView, subRoute: API_POLICIES_SUB_PATH }
        ]
    },
    { href: "Process/List", activeLinkName: LinkNames.process, label: FRENDS.Localizations.Processes, permission: Permissions.ProcessView },
    { href: "SubProcess/List", activeLinkName: LinkNames.subprocess, label: FRENDS.Localizations.Subprocesses, permission: Permissions.ProcessView },
    { href: "ProcessTemplates", activeLinkName: LinkNames.templates, label: "Templates", permission: Permissions.ProcessTemplateView },
    { href: "EnvironmentVariables/List", activeLinkName: LinkNames.environmentvariables, label: FRENDS.Localizations.EnvironmentVariables, permission: Permissions.EnvironmentVariablesEdit },
    {
        href: "", activeLinkName: LinkNames.administration, label: FRENDS.Localizations.Administration, permission: Permissions.AdministrationView,
        children: [
            { href: "Environment", activeLinkName: LinkNames.environment, label: FRENDS.Localizations.Environments, permission: Permissions.EnvironmentEdit },
            { href: "Task", activeLinkName: LinkNames.task, label: FRENDS.Localizations.Tasks, permission: Permissions.TaskView },
            { href: "MonitoringRules/List", activeLinkName: LinkNames.monitoringrules, label: FRENDS.Localizations.MonitoringRules, permission: Permissions.MonitoringRulesView },
            { href: "UserManagement", activeLinkName: LinkNames.usermanagement, label: FRENDS.Localizations.UserManagement, permission: Permissions.UserManagementAdmin },
            { href: "ApiKeyManagement", activeLinkName: LinkNames.apikeymanagement, label: FRENDS.Localizations.Administration_ApiKeys, permission: Permissions.ApiKeyManagementAdmin },
            { href: "OAuthApplicationManagement", activeLinkName: LinkNames.oauthapplicationmanagement, label: "OAuth Applications", permission: Permissions.OAuthApplicationManagementAdmin },
            { href: "PrivateApplication", activeLinkName: LinkNames.privateapplication, label: "Private Applications", permission: Permissions.PrivateApplicationView },
            { href: "AiAuditLog", activeLinkName: LinkNames.aiauditlog, label: "AI Audit Log", permission: Permissions.AiView }
        ]
    }
];

export type INavigationItemsProps = INavigationProps &
{
    hasRightsFor: { [key: string]: boolean },
    currentAgentGroup: string | undefined,
    currentEnvironment: string | undefined,
    asColumn?: boolean,
}

export interface IPathParams extends AgentGroupEnvironment {
    base?: string;
    processType?: string;
    action?: string;
    id?: string;
    instances?: string
}

export interface INavigationProps {
    isAuthenticated: boolean;
    username: string;
    intercomAppId: string;
    route: string;
    activeLink: LinkNames | null;
}
export interface INavigationBarData {
    href: string;
    activeLinkName: LinkNames;
    permission: string;
    label: string;
    /**
     * The subRoute property represents a route inside the page router
     */
    subRoute?: string;
    children?: INavigationBarData[];
}

export interface IAgentGroupData {
    agentGroupHash: HashMap<AgentGroup>;
    agentGroupList: AgentGroup[]
}

type Visibility = 'visible' | 'hidden';

export default App;